import { Assert } from '@cotera/utilities';
import { AssocEntry, EraQlAst } from '../eraql-ast/ast';
import { WithMeta } from '../lex';
import { withNullMeta } from '../lex/types';

/**
 * Nullifies all the withMeta ranges, to make comparing printed EraQLAst easier.
 *
 * Eg: console.log(JSON.stringify(normalizeEraQLAst(astWithMeta), null, 2))
 * Will pretty print the ast; and differences can easily be spotted with a text-comparison tool.
 */
export function normalizeEraQLAst(
  astWithMeta: WithMeta<EraQlAst>
): WithMeta<EraQlAst> {
  const [ast, _] = astWithMeta;

  switch (ast.t) {
    case 'symbol':
    case 'literal':
    case 'ident': {
      return withNullMeta(ast);
    }

    case 'call': {
      const {
        op: [op, _],
        args,
        display,
      } = ast;

      const normalizedOp =
        op.t === 'operator' || op.t === 'tag'
          ? withNullMeta(op)
          : normalizeEraQLAst(withNullMeta(op));

      return withNullMeta({
        t: 'call',
        op: normalizedOp,
        args: args.map(normalizeEraQLAst),
        display,
      });
    }

    case 'assoc': {
      const { entries } = ast;

      const normalizedEntries: WithMeta<AssocEntry>[] = entries.map(
        ([entry, _]) => {
          switch (entry.t) {
            case 'assoc-kv': {
              const {
                key: [key, _1],
                val,
              } = entry;
              return withNullMeta({
                t: 'assoc-kv',
                key: withNullMeta(key),
                val: normalizeEraQLAst(val),
              });
            }
            case 'assoc-spread':
              return withNullMeta({
                t: 'assoc-spread',
                val: normalizeEraQLAst(entry.val),
              });
            default:
              return Assert.unreachable(entry);
          }
        }
      );

      return withNullMeta({ t: 'assoc', entries: normalizedEntries });
    }

    case 'list': {
      const { elems } = ast;
      return withNullMeta({ t: 'list', elems: elems.map(normalizeEraQLAst) });
    }

    case 'let': {
      const { sym, bindingExpr, body } = ast;
      return withNullMeta({
        t: 'let',
        sym: nullifyMeta(sym),
        bindingExpr: normalizeEraQLAst(bindingExpr),
        body: normalizeEraQLAst(body),
      });
    }

    case 'attr-access':
      return withNullMeta({
        t: 'attr-access',
        in: normalizeEraQLAst(ast.in),
        attr: nullifyMeta(ast.attr),
      });

    case 'closure': {
      const {
        args: [args, _],
        body,
      } = ast;

      const normalizedArgs = args.map(({ sym }) => ({ sym: nullifyMeta(sym) }));

      return withNullMeta({
        t: 'closure',
        args: withNullMeta(normalizedArgs),
        body: normalizeEraQLAst(body),
      });
    }

    default:
      return Assert.unreachable(ast);
  }
}

const nullifyMeta = <T>([data, _]: WithMeta<T>): WithMeta<T> =>
  withNullMeta(data);
